import React from 'react';
import { useRTC } from 'app/containers/rtc/Provider';
import { Peer, usePeers } from 'app/containers/rtc/PeersContext';
import { useI18n } from 'app/components/localization';
import CountdownMessage from 'app/components/call/CallErrors/CountdownMessage';
import MarginGroup from 'app/components/layout/MarginGroup';
import { Severity } from 'app/components/call/CallErrors/CallErrorMessage';
import { usePresenceContext } from 'app/containers/rtc/PresenceContext';
import { tFunctionType } from 'app/services/localization';

interface IProps {
  peer: Peer;
  reconnect: (id: Peer['id']) => void;
  participantName: string;
  t: tFunctionType;
}

/**
 * Shows a message that the connection is taking longer than
 * anticipated, with a countdown until the next automatic reconnect +
 * a button to do it immediately.
 */
const PeerTimeoutWarning = React.memo<IProps>(({ peer, t, reconnect, participantName }) => {
  if (!peer.timeoutWarning) {
    return null;
  }

  return (
    <CountdownMessage
      severity={Severity.Info}
      timeoutWarning={peer.timeoutWarning}
      buttonLabel={t('rtc.errors.timeout.reconnect-now')}
      buttonAction={() => reconnect(peer.id)}
    >
      {(count) => <MarginGroup>
        {t('rtc.errors.timeout.message.peer', { name: participantName })}
        {t('rtc.errors.timeout.message.trying', { count })}
      </MarginGroup>}
    </CountdownMessage>
  );
});

const PeerTimeoutWarnings = () => {
  const peers = usePeers();
  const rtc = useRTC();
  const { t } = useI18n();
  const { getParticipantName } = usePresenceContext();

  if (!rtc) {
    return null;
  }

  return (
    <>
      {peers.valueSeq().map((peer, id) => (
        <PeerTimeoutWarning
          key={id}
          peer={peer}
          t={t}
          reconnect={rtc.resetPeerConnection}
          participantName={getParticipantName(peer.id)}
        />
      ))}
    </>
  );
};

export default PeerTimeoutWarnings;
